import { uniq } from "lodash";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { getSearchParams, getUpdatedSearchParamsUrl } from "../../utils/search-params";
import { Container } from "../container";
import { Select } from "../forms";
import { Icon } from "../icon";
import { FileCard } from "./file-card";

export interface FileListProps extends Queries.FileCategoryDataFragment {
  years?: number[];
  labels: Record<string, string | null>;
  defaultMaxFilesShown?: number;
}

export const FileList: FC<FileListProps> = ({
  files: categoryFiles,
  name,
  slug,
  years: _years,
  labels,
  defaultMaxFilesShown = 6,
}) => {
  const searchParams = getSearchParams();
  const searchParamYear = searchParams?.get(`year-${slug}`);
  const years = useMemo(() => {
    const defaultYears = (categoryFiles ?? []).map((file) => file?.year ?? 0).filter(Boolean);
    if (searchParamYear) defaultYears.push(+searchParamYear);
    if (defaultYears.length === 0) defaultYears.push(new Date().getFullYear());

    return (_years ?? uniq(defaultYears)).sort().reverse();
  }, [_years, categoryFiles, searchParamYear]);

  const [year, _setYear] = useState(
    searchParamYear ? +searchParamYear : years[0] ?? new Date().getFullYear()
  );

  const [isShowingAllFiles, setIsShowingAllFiles] = useState(false);

  const filesByYear = useMemo(() => {
    return (categoryFiles ?? []).filter((file) => {
      return file?.year === year;
    });
  }, [categoryFiles, year]);

  const files = useMemo(() => {
    if (isShowingAllFiles) return filesByYear;
    else return filesByYear.slice(0, defaultMaxFilesShown);
  }, [isShowingAllFiles, filesByYear, defaultMaxFilesShown]);

  const setYear = useCallback(
    async (year: number) => {
      _setYear(year);
      if (typeof window !== "undefined") {
        const path = getUpdatedSearchParamsUrl({ [`year-${slug}`]: year });
        window.history.pushState({ path }, "", path);
      }
    },
    [slug]
  );

  useEffect(() => {
    setIsShowingAllFiles(false);
  }, [year]);

  return (
    <Container containerClassName="py-6" pagePaddingX>
      {name && <h3 className="text-primary text-4xl font-medium mb-4">{name}</h3>}
      {/* Year selector */}
      {years.length > 0 && (
        <Select
          className="w-full lg:w-44 mb-4"
          label={labels["file-list-year-select"]}
          value={year}
          onChange={(e) => setYear(+e.target.value)}
        >
          {years.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
      )}

      <div className="flex flex-col gap-2">
        {files.map((file, i) => {
          if (!file) return null;
          return <FileCard {...file} key={i} />;
        })}
        {!files.length && <div>{labels["file-list-no-files-found"]}</div>}
      </div>

      {!isShowingAllFiles && filesByYear.length !== files.length && (
        <button
          className="flex items-center gap-2 mt-4"
          onClick={() => setIsShowingAllFiles(true)}
          disabled={isShowingAllFiles}
        >
          <Icon icon="circle-plus" className="text-primary w-8 h-8" />
          <span className="text-primary">{labels["file-list-show-previous"]}</span>
        </button>
      )}
    </Container>
  );
};
